import { useEffect, useMemo, useState, useCallback } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api, { errorMessage } from "../../lib/api";
import { Modal } from "../Modal";
import { FormField, inputClass, ActionButton, GhostButton, PillBadge } from "../Primitives";
import { Send, MapPin, Users2, Search, X, BookmarkPlus, Trash2, ChevronDown } from "lucide-react";
import { toast } from "sonner";

// Same pulsing pin used on the Live Map so the visual language stays consistent.
const DISPATCH_ICON = new L.DivIcon({
  className: "",
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  html: '<div style="width:24px;height:24px;border-radius:50%;background:#0A84FF;border:3px solid #ffffff;box-shadow:0 0 0 4px rgba(10,132,255,0.25);"></div>',
});

/** Small helper: capture map clicks and forward the latlng to the parent. */
function PickHandler({ onPick }) {
  useMapEvents({
    click(e) { onPick({ lat: e.latlng.lat, lng: e.latlng.lng }); },
  });
  return null;
}

/**
 * New Dispatch Blast modal — launched from the Dispatch Log page.
 * Lets operators pick guards (on-shift or all-active), drop an optional pin
 * on a mini-map, compose a subject + message, choose channels, and POST to
 * /api/dispatch/broadcast — the same endpoint the Live Map uses.
 */
export function NewDispatchModal({ onClose, onSent, restrictToSite = null, prefill = null }) {
  const [onShiftOnly, setOnShiftOnly] = useState(true);
  const [onShift, setOnShift] = useState([]);      // /live-map/officers rows
  const [allGuards, setAllGuards] = useState([]);  // /guards rows (active)
  const [siteGuardIds, setSiteGuardIds] = useState(null); // Set<guard_id> | null (null = no restriction)
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [subject, setSubject] = useState(prefill?.subject || "");
  const [message, setMessage] = useState(prefill?.message || "");
  const [channels, setChannels] = useState(new Set(["push", "email"]));
  const [pin, setPin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  // Templates: quick-apply reusable subject/message/channels combos.
  const [templates, setTemplates] = useState([]);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Fetch both pools in parallel + the template list. When restricted to a
  // site, we derive the on-duty guard set from `/live-map/officers`
  // (guards currently on shift AT that specific site right now) — this
  // matches the operator's intent when dispatching from an incident on
  // that site: reach the people actually there, not the whole roster
  // ever assigned.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [o, g, t] = await Promise.all([
          api.get("/live-map/officers"),
          api.get("/guards"),
          api.get("/dispatch/templates").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setOnShift(o.data || []);
        setAllGuards((g.data || []).filter((x) => (x.status || "active") === "active" && !x.deactivation_date));
        setTemplates(t.data || []);
        if (restrictToSite) {
          const onDuty = (o.data || []).filter((x) => x.site_id === restrictToSite.id);
          const ids = new Set(onDuty.map((x) => x.guard_id));
          setSiteGuardIds(ids);
          // Lock the toggle to on-shift and auto-pre-select the on-duty
          // pool — the dispatcher can uncheck individuals but shouldn't
          // accidentally page off-duty roster members.
          setOnShiftOnly(true);
          setSelected(ids);
        }
      } catch (e) {
        if (!cancelled) toast.error(errorMessage(e, "Failed to load "));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [restrictToSite]);

  const applyTemplate = (t) => {
    setSubject(t.subject || "");
    setMessage(t.message || "");
    setChannels(new Set((t.channels && t.channels.length) ? t.channels : ["push", "email"]));
    setTemplatePickerOpen(false);
    toast.success(`Applied template: ${t.name}`);
  };

  const saveAsTemplate = async () => {
    if (!message.trim()) return toast.error("Type a message before saving as template");
    const name = window.prompt("Template name (e.g. \"Alarm response · Sector B\"):", subject || "");
    if (!name || !name.trim()) return;
    setSavingTemplate(true);
    try {
      const r = await api.post("/dispatch/templates", {
        name: name.trim(),
        subject: subject.trim() || null,
        message: message.trim(),
        channels: [...channels],
      });
      setTemplates((prev) => [...prev, r.data].sort((a, b) => a.name.localeCompare(b.name)));
      toast.success("Template saved");
    } catch (e) { toast.error(errorMessage(e, "Save template failed")); }
    finally { setSavingTemplate(false); }
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/dispatch/templates/${t.id}`);
      setTemplates((prev) => prev.filter((x) => x.id !== t.id));
      toast.success("Template deleted");
    } catch (e) { toast.error(errorMessage(e, "Delete failed")); }
  };

  // Union the two pools into a normalized guard list. On-shift wins (has
  // site context); otherwise fall back to the roster row. When the modal
  // is restricted to a site, both pools are further narrowed to guards
  // currently on-duty at that site (from /live-map/officers).
  const pool = useMemo(() => {
    const restrict = siteGuardIds;
    if (onShiftOnly) {
      return onShift
        .filter((o) => !restrict || restrict.has(o.guard_id))
        .map((o) => ({
          id: o.guard_id,
          name: o.guard_name || "Unknown",
          badge_no: o.badge_no,
          site_name: o.site_name,
          onShift: true,
        }));
    }
    // "All active" — annotate anyone currently on-shift with a site badge.
    const onShiftMap = new Map(onShift.map((o) => [o.guard_id, o]));
    return allGuards
      .filter((g) => !restrict || restrict.has(g.id))
      .map((g) => ({
        id: g.id,
        name: g.name || "Unknown",
        badge_no: g.badge_no,
        site_name: onShiftMap.get(g.id)?.site_name,
        onShift: onShiftMap.has(g.id),
      }));
  }, [onShiftOnly, onShift, allGuards, siteGuardIds]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pool;
    return pool.filter((g) =>
      (g.name || "").toLowerCase().includes(q) ||
      (g.badge_no || "").toLowerCase().includes(q) ||
      (g.site_name || "").toLowerCase().includes(q)
    );
  }, [pool, search]);

  const toggleGuard = (gid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid); else next.add(gid);
      return next;
    });
  };
  const toggleChannel = (ch) => {
    setChannels((prev) => {
      const next = new Set(prev);
      if (next.has(ch)) next.delete(ch); else next.add(ch);
      return next;
    });
  };
  const selectAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      filtered.forEach((g) => next.add(g.id));
      return next;
    });
  };
  const clearSelection = useCallback(() => setSelected(new Set()), []);

  const submit = async () => {
    const guard_ids = [...selected];
    if (guard_ids.length === 0) return toast.error("Select at least one ");
    if (!message.trim()) return toast.error("Message is required");
    if (channels.size === 0) return toast.error("Pick at least one channel (Push or Email)");
    setSending(true);
    try {
      const body = {
        guard_ids,
        message: message.trim(),
        channels: [...channels],
      };
      if (subject.trim()) body.subject = subject.trim();
      if (pin) { body.lat = pin.lat; body.lng = pin.lng; }
      const r = await api.post("/dispatch/broadcast", body);
      const { push_sent = 0, email_sent = 0 } = r.data || {};
      toast.success(`Dispatch sent — ${push_sent} push · ${email_sent} email`);
      onSent && onSent();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e, "Dispatch failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal onClose={onClose} title="New Dispatch Blast" testId="new-dispatch-modal">
      <div className="space-y-4">
        {restrictToSite && (
          <div
            data-testid="dispatch-site-restriction"
            className="border border-[#FF9F0A]/40 bg-[#FF9F0A]/5 px-3 py-2 flex items-center gap-2 text-xs"
          >
            <MapPin className="size-3.5 text-[#FF9F0A] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[#FF9F0A]">On-duty at site</div>
              <div className="text-zinc-200 font-medium truncate">{restrictToSite.name}</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">
                Only  currently clocked in at this location can receive this blast.
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
              {siteGuardIds ? `${siteGuardIds.size} guards` : "…"}
            </div>
          </div>
        )}

        {/* Templates picker — quick-apply reusable subject/message/channels combos */}
        <div className="border border-[#27272A] bg-[#0f0f10]">
          <button
            type="button"
            data-testid="dispatch-templates-toggle"
            onClick={() => setTemplatePickerOpen((v) => !v)}
            className="w-full flex items-center justify-between px-3 py-2 hover:bg-[#1a1a1c] transition-colors"
          >
            <div className="label-mute inline-flex items-center gap-2">
              <BookmarkPlus className="size-3" /> Templates ({templates.length})
            </div>
            <ChevronDown className={`size-3.5 text-zinc-500 transition-transform ${templatePickerOpen ? "rotate-180" : ""}`} />
          </button>
          {templatePickerOpen && (
            <div className="border-t border-[#27272A] max-h-[140px] overflow-y-auto" data-testid="dispatch-templates-list">
              {templates.length === 0 && (
                <div className="p-3 text-[11px] text-zinc-500 italic">
                  No templates yet. Compose a message below then click <span className="text-[#0A84FF] font-bold">Save as template</span>.
                </div>
              )}
              {templates.map((t) => (
                <div
                  key={t.id}
                  data-testid={`dispatch-template-${t.id}`}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-[#1c1c1e] border-b border-[#27272A] last:border-b-0"
                >
                  <button
                    type="button"
                    onClick={() => applyTemplate(t)}
                    data-testid={`dispatch-template-apply-${t.id}`}
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="text-sm font-medium truncate">{t.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 truncate">{t.subject || "—"} · {t.message}</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteTemplate(t)}
                    data-testid={`dispatch-template-delete-${t.id}`}
                    className="text-zinc-500 hover:text-[#FF3B30] p-1"
                    aria-label={`Delete template ${t.name}`}
                    title="Delete template"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Officer picker */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="label-mute inline-flex items-center gap-1"><Users2 className="size-3" /> Officers ({selected.size} selected)</div>
            <label
              className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-widest ${restrictToSite ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
              data-testid="dispatch-onshift-toggle"
              title={restrictToSite ? "Locked: site restriction only reaches on-duty guards" : undefined}
            >
              <input
                type="checkbox"
                className="accent-[#0A84FF]"
                checked={onShiftOnly}
                disabled={!!restrictToSite}
                onChange={(e) => setOnShiftOnly(e.target.checked)}
              />
              On-shift only
            </label>
          </div>
          <div className="relative">
            <Search className="size-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="dispatch-guard-search"
              placeholder="Search by name, badge, or site…"
              className={`${inputClass} pl-8`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="mt-2 flex items-center gap-2 text-[10px] uppercase tracking-widest">
            <button type="button" onClick={selectAllFiltered} data-testid="dispatch-select-all"
              className="text-zinc-400 hover:text-[#0A84FF]">Select {filtered.length} filtered</button>
            <span className="text-zinc-700">·</span>
            <button type="button" onClick={clearSelection} data-testid="dispatch-clear-selection"
              className="text-zinc-400 hover:text-[#FF3B30]">Clear</button>
          </div>
          <div className="mt-2 border border-[#27272A] max-h-[180px] overflow-y-auto divide-y divide-[#27272A]" data-testid="dispatch-guard-list">
            {loading && <div className="p-4 text-center text-zinc-500 text-xs scanlines">Loading …</div>}
            {!loading && filtered.length === 0 && (
              <div className="p-6 text-center text-zinc-500 text-xs">
                {onShiftOnly ? "No guards currently on-shift. Toggle off to reach the full active roster." : "No guards match your search."}
              </div>
            )}
            {filtered.map((g) => {
              const isSel = selected.has(g.id);
              return (
                <label
                  key={g.id}
                  data-testid={`dispatch-guard-${g.id}`}
                  className={`flex items-center gap-3 p-2.5 cursor-pointer hover:bg-[#1c1c1e] ${isSel ? "bg-[#0A84FF]/5" : ""}`}
                >
                  <input
                    type="checkbox"
                    className="accent-[#0A84FF]"
                    checked={isSel}
                    onChange={() => toggleGuard(g.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{g.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 truncate">
                      {g.badge_no || "—"}{g.site_name ? ` · ${g.site_name}` : ""}
                    </div>
                  </div>
                  {g.onShift && <PillBadge tone="green">On shift</PillBadge>}
                </label>
              );
            })}
          </div>
        </div>

        {/* Location picker */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="label-mute inline-flex items-center gap-1"><MapPin className="size-3" /> Location (optional — click map to drop pin)</div>
            {pin && (
              <button
                type="button"
                data-testid="dispatch-clear-pin"
                onClick={() => setPin(null)}
                className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FF3B30] inline-flex items-center gap-1"
              >
                <X className="size-3" /> Clear pin
              </button>
            )}
          </div>
          <div className="border border-[#27272A]" style={{ height: 220 }} data-testid="dispatch-map-picker">
            <MapContainer center={[39.8283, -98.5795]} zoom={4} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
              <TileLayer attribution="&copy; OSM" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <PickHandler onPick={setPin} />
              {pin && <Marker position={[pin.lat, pin.lng]} icon={DISPATCH_ICON} />}
            </MapContainer>
          </div>
          {pin && (
            <div className="mt-1 font-mono text-[10px] text-zinc-500" data-testid="dispatch-pin-coords">
              {pin.lat.toFixed(5)}, {pin.lng.toFixed(5)}
            </div>
          )}
        </div>

        <FormField label="Subject (optional)">
          <input
            data-testid="dispatch-subject-input"
            className={inputClass}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g. Alarm response · Perimeter breach"
            maxLength={120}
          />
        </FormField>

        <FormField label={`Message * (${message.length}/500)`}>
          <textarea
            data-testid="dispatch-message-input"
            rows={3}
            className={inputClass}
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 500))}
            placeholder="What do you need the  to do?"
          />
        </FormField>

        <div>
          <div className="label-mute mb-2">Channels</div>
          <div className="flex items-center gap-2">
            {[
              { key: "push", label: "Push notification" },
              { key: "email", label: "Email" },
            ].map(({ key, label }) => {
              const active = channels.has(key);
              return (
                <label
                  key={key}
                  data-testid={`dispatch-channel-${key}`}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 border cursor-pointer text-xs uppercase tracking-widest transition-colors ${active ? "border-[#0A84FF] text-[#0A84FF] bg-[#0A84FF]/5" : "border-[#27272A] text-zinc-500 hover:text-white"}`}
                >
                  <input
                    type="checkbox"
                    className="accent-[#0A84FF]"
                    checked={active}
                    onChange={() => toggleChannel(key)}
                  />
                  {label}
                </label>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#27272A]">
          <GhostButton
            type="button"
            data-testid="dispatch-save-as-template"
            onClick={saveAsTemplate}
            disabled={savingTemplate || !message.trim()}
            title="Save current subject / message / channels as a reusable template"
          >
            <BookmarkPlus className="size-3.5" /> {savingTemplate ? "Saving…" : "Save as template"}
          </GhostButton>
          <div className="flex-1" />
          <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
          <ActionButton
            data-testid="dispatch-send"
            onClick={submit}
            disabled={sending || selected.size === 0 || !message.trim() || channels.size === 0}
          >
            <Send className="size-4" /> {sending ? "Sending…" : `Send to ${selected.size}`}
          </ActionButton>
        </div>
      </div>
    </Modal>
  );
}
